/**
 * Stacked-bar mixed-segment tooltip: distribution of inner labels within mixed reviews.
 * Percentages use the sum of inner counts as denominator (sums to ~100%).
 */

function readEntryLabel(entry, labelKey) {
  if (!entry || typeof entry !== "object") {
    return "";
  }
  if (labelKey && entry[labelKey] != null) {
    return String(entry[labelKey]).trim();
  }
  const fallback =
    entry.intent ?? entry.label ?? entry.sentiment ?? entry.name ?? "";
  return String(fallback).trim();
}

export function computeMixedInnerTooltipBreakdown(
  mixedReviewCount,
  entries,
  options = {},
) {
  const n = Number(mixedReviewCount);
  if (!Number.isFinite(n) || n <= 0) {
    return [];
  }

  const labelKey = options.labelKey;
  const excludeLabels = new Set(
    (options.excludeLabels || ["mixed"]).map((value) =>
      String(value).trim().toLowerCase(),
    ),
  );

  let denominator = options.denominator || "innerTotal";
  const useMixedReviewCount = denominator === "mixedReviewCount";

  const parsed = (Array.isArray(entries) ? entries : [])
    .map((entry) => {
      const label = readEntryLabel(entry, labelKey);
      if (!label || excludeLabels.has(label.toLowerCase())) {
        return null;
      }
      const count = Number(entry.count);
      if (!Number.isFinite(count) || count <= 0) {
        return null;
      }
      return { label, count };
    })
    .filter(Boolean);

  if (useMixedReviewCount) {
    return parsed
      .map((row) => {
        const percentage = Math.round((row.count / n) * 100);
        if (percentage <= 0) {
          return null;
        }
        return { label: row.label, count: row.count, percentage };
      })
      .filter(Boolean)
      .sort(
        (a, b) =>
          b.percentage - a.percentage ||
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
      );
  }

  const innerTotal = parsed.reduce((sum, row) => sum + row.count, 0);
  if (innerTotal <= 0) {
    return [];
  }

  return parsed
    .map((row) => {
      const percentage = Math.round((row.count / innerTotal) * 100);
      if (percentage <= 0) {
        return null;
      }
      return { label: row.label, count: row.count, percentage };
    })
    .filter(Boolean)
    .sort(
      (a, b) =>
        b.percentage - a.percentage ||
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
    );
}

export function resolveMixedInnerTooltipSource(rowPayload, options = {}) {
  const entriesKey = options.entriesKey || "intents";
  if (!rowPayload || typeof rowPayload !== "object") {
    return { mixedReviewCount: 0, mixedPercentage: 0, entries: [] };
  }
  const mixedReviewCount = Number(rowPayload.mixed_review_count);
  const mixedPercentage = Number(rowPayload.mixed_percentage);
  const entries = Array.isArray(rowPayload[entriesKey])
    ? rowPayload[entriesKey]
    : [];
  return {
    mixedReviewCount: Number.isFinite(mixedReviewCount) ? mixedReviewCount : 0,
    mixedPercentage: Number.isFinite(mixedPercentage) ? mixedPercentage : 0,
    entries,
  };
}
